import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getSchool,
  createSchool,
  updateSchool,
  updateSchoolAccount,
  getSchoolContacts,
  createSchoolContact,
  updateSchoolContact,
  deleteSchoolContact,
  updateSchoolAccountAdmin,
  getSchoolSubscriptions,
  addSchoolSubscription,
  updateSchoolSubscription,
  deleteSchoolSubscription,
} from '../api/schools-api';
import { listProducts } from '../api/products-api';
import { listAdvocates } from '../api/advocates-api';
import { useAsync } from '../hooks/useAsync';
import { StatusBadge, LoadingBlock, ErrorBanner } from '../components/ui';
import { EntityForm, fieldsToReadItems, type FieldDef } from '../components/EntityForm';
import { DetailSection, EditableSection, DefinitionList, type ReadItem } from '../components/DetailSection';
import { ContactsTab } from '../components/ContactsTab';
import { SubscriptionsTab } from '../components/SubscriptionsTab';
import { AccountPanel } from '../components/AccountPanel';
import { CreateEntity } from '../components/CreateEntity';
import { NearbyModal } from '../components/NearbyModal';
import { Button } from '../neon/controls';
import { contactName } from '../utils/format';
import { config } from '../runtime-config';
import { getSiblings } from '../utils/browse-context';
import { NeonBreadcrumbs } from '@ps-refarch-ux/neon';

const SCHOOL_TYPE_OPTIONS = [
  { value: 0, label: 'Public' },
  { value: 1, label: 'Private' },
];

const INFO_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'school_type', label: 'Type', type: 'select', options: SCHOOL_TYPE_OPTIONS },
  { key: 'school_level', label: 'School level (code)', type: 'number' },
  { key: 'address_line1', label: 'Address 1' },
  { key: 'address_line2', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'country', label: 'Country' },
  { key: 'phone', label: 'Phone' },
  { key: 'fax', label: 'Fax' },
  { key: 'url', label: 'Website' },
  { key: 'enrollment', label: 'Enrollment', type: 'number' },
  { key: 'lowest_grade', label: 'Lowest grade' },
  { key: 'highest_grade', label: 'Highest grade' },
  { key: 'nces_id', label: 'NCES ID' },
  { key: 'ceeb_code', label: 'CEEB code' },
  { key: 'sis_id', label: 'SIS ID' },
];

const CREATE_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'country', label: 'Country' },
  { key: 'school_type', label: 'School type (code)', type: 'number' },
];

export function HighSchoolDetailPage() {
  const { nid = '' } = useParams();
  const navigate = useNavigate();

  if (nid === 'new') {
    return (
      <CreateEntity
        title="New high school"
        fields={CREATE_FIELDS}
        create={async (body) => {
          const { school } = await createSchool(body);
          return school.nid;
        }}
        onCreated={(id) => navigate(`/highschools/${encodeURIComponent(id)}`)}
        backTo="/highschools"
      />
    );
  }

  return <HighSchoolDetail nid={nid} navigateBack={() => navigate('/highschools')} />;
}

function HighSchoolDetail({ nid, navigateBack }: { nid: string; navigateBack: () => void }) {
  const navigate = useNavigate();
  const { prev, next } = getSiblings('highschools', nid);
  const [nearbyOpen, setNearbyOpen] = useState(false);
  const school = useAsync(() => getSchool(nid), [nid]);
  const contacts = useAsync(() => getSchoolContacts(nid), [nid]);
  const subscriptions = useAsync(() => getSchoolSubscriptions(nid), [nid]);
  const products = useAsync(() => listProducts(), []);
  const advocates = useAsync(() => listAdvocates(), []);

  if (school.loading && !school.data) return <LoadingBlock label="Loading school…" />;
  if (school.error) return <ErrorBanner error={school.error} />;
  const detail = school.data?.school;
  if (!detail) return <ErrorBanner error="School not found." />;

  const contactList = contacts.data?.contacts ?? [];

  const contactOptions = contactList.map((c) => ({ value: c.id, label: `${contactName(c)} (#${c.id})` }));
  const contactById = (id: any) => {
    const c = contactList.find((x) => String(x.id) === String(id));
    return c ? `${contactName(c)} (#${c.id})` : '—';
  };
  const adminFields: FieldDef[] = [
    { key: 'primary_contact_id', label: 'Primary contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'technical_contact_id', label: 'Technical contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'sales_contact_id', label: 'Sales contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'billing_contact_id', label: 'Billing contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
  ];
  const idItems: ReadItem[] = [
    { label: 'Naviance ID', value: detail.nid },
    { label: 'Institution UUID', value: detail.institution_uuid || '—' },
    { label: 'NCES ID', value: detail.nces_id || '—' },
    {
      label: 'Website',
      value: detail.url ? (
        <a
          href={/^https?:\/\//.test(detail.url) ? detail.url : `https://${detail.url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {detail.url}
        </a>
      ) : (
        '—'
      ),
    },
    {
      label: 'District',
      value: detail.district_name ? (
        <Link to={`/districts/${encodeURIComponent(detail.district_id)}`}>{detail.district_name}</Link>
      ) : (
        'N/A'
      ),
    },
  ];
  const adminReadItems: ReadItem[] = [
    { label: 'Primary contact', value: contactById(detail.primary_contact_id) },
    { label: 'Technical contact', value: contactById(detail.technical_contact_id) },
    { label: 'Sales contact', value: contactById(detail.sales_contact_id) },
    { label: 'Billing contact', value: contactById(detail.billing_contact_id) },
  ];

  return (
    <div className="page">
      <div className="detail-header">
        <div>
          <NeonBreadcrumbs
            id="hs-breadcrumbs"
            breadcrumbs={[
              {
                text: 'High Schools',
                url: '/highschools',
                onUserClick: (e: Event) => {
                  e.preventDefault();
                  navigate('/highschools');
                },
              },
              { text: detail.name, url: '#' },
            ]}
          />
          <h2>
            {detail.name} <StatusBadge status={detail.accountStatus} />
          </h2>
          <div className="subtle">
            {detail.nid} · {detail.city}, {detail.state}
            {detail.district_name ? (
              <>
                {' '}
                · District:{' '}
                <Link to={`/districts/${encodeURIComponent(detail.district_id)}`}>{detail.district_name}</Link>
              </>
            ) : null}
          </div>
        </div>
        <div className="detail-actions">
          {prev ? (
            <Button id="hs-prev" text="Previous" variant="tertiary" icon="chevron-left" onClick={() => navigate(`/highschools/${encodeURIComponent(prev)}`)} />
          ) : null}
          {next ? (
            <Button id="hs-next" text="Next" variant="tertiary" icon="chevron-right" iconPosition="right" onClick={() => navigate(`/highschools/${encodeURIComponent(next)}`)} />
          ) : null}
          {detail.institution_uuid ? (
            <Button
              id="hs-signin"
              text="Sign in to account"
              variant="secondary"
              icon="external-link"
              href={`${config.adminLoginUrl}?highschoolId=${encodeURIComponent(detail.institution_uuid)}`}
              openInNewTab
            />
          ) : null}
          <Button id="hs-back" text="Back" variant="tertiary" icon="arrow-left" onClick={navigateBack} />
        </div>
      </div>

      <div className="detail-sections">
        <DetailSection
          title="Summary"
          actions={<Button id="hs-nearby" text="Find nearby" variant="tertiary" icon="location" onClick={() => setNearbyOpen(true)} />}
        >
          <DefinitionList items={idItems} />
        </DetailSection>

        <EditableSection
          title="High School Overview"
          read={<DefinitionList items={fieldsToReadItems(INFO_FIELDS, detail)} />}
          renderEdit={(done) => (
            <EntityForm
              fields={INFO_FIELDS}
              initial={detail}
              save={(patch) => updateSchool(nid, patch)}
              onSaved={() => {
                school.reload();
                done();
              }}
              onCancel={done}
            />
          )}
        />

        <AccountPanel
          idPrefix="hs"
          detail={detail}
          advocates={advocates.data?.advocates ?? []}
          showSubscriptionStatus
          onUpdateAccount={(patch) => updateSchoolAccount(nid, patch)}
          onChanged={() => school.reload()}
        />

        <SubscriptionsTab
          subscriptions={subscriptions.data?.subscriptions ?? []}
          products={products.data?.products ?? []}
          loading={subscriptions.loading}
          onReload={subscriptions.reload}
          add={(body) => addSchoolSubscription(nid, body)}
          update={(pid, body) => updateSchoolSubscription(nid, pid, body)}
          remove={(pid) => deleteSchoolSubscription(nid, pid)}
          showEnrollment
        />

        <ContactsTab
          contacts={contactList}
          loading={contacts.loading}
          onReload={contacts.reload}
          create={(body) => createSchoolContact(nid, body)}
          update={(id, body) => updateSchoolContact(nid, id, body)}
          remove={(id) => deleteSchoolContact(nid, id)}
        />

        <EditableSection
          title="Key Contacts"
          read={<DefinitionList items={adminReadItems} />}
          renderEdit={(done) => (
            <EntityForm
              fields={adminFields}
              initial={detail}
              save={(patch) => updateSchoolAccountAdmin(nid, patch)}
              submitLabel="Save key contacts"
              onSaved={() => {
                school.reload();
                done();
              }}
              onCancel={done}
            />
          )}
        />
      </div>

      {nearbyOpen && <NearbyModal nid={nid} schoolName={detail.name} onClose={() => setNearbyOpen(false)} />}
    </div>
  );
}
